/**
 * 大六壬 · 神煞
 *
 * MVP 清单 20 项（取法均按通行版本，注释标注依据）：
 *   天乙贵人、天德、月德、驿马、桃花、天喜、羊刃、飞刃、破碎、华盖、
 *   将星、劫煞、灾煞、天马、红鸾、天哭、病符、死符、孤辰、寡宿
 *
 * 神煞按「年支 / 月支 / 日支 / 日干」四类依据取，落在三传或四课上神者加注标记，
 * 供断课规则引擎加权。
 */
import { GAN, YANG_REN, ZHI, feiRen, guGua, ganOf, hongLuan, poSui, sanHeJu, SAN_HE_SHEN_SHA, siFu, bingFu, tianMa, tianXi, tianKu, TIAN_DE, YUE_DE, guiRen, isDayTime, zhiOf, } from './constants';
/** 取某地支所属三合局的索引（0..3） */
function juOf(zhiIdx) {
    return sanHeJu(zhiIdx);
}
/**
 * 排神煞。
 * @returns 神煞列表（按 luck 绝对值降序，即影响大者在前）
 */
export function buildShenSha(ctx) {
    const { nianZhiIndex, yueZhiIndex, dayZhiIndex, dayGanIndex, zhanShiIndex, chuanZhi, keShang } = ctx;
    const chuanSet = new Set(chuanZhi);
    const keSet = new Set(keShang);
    /** 内部构造：{ zhi } 为地支索引；{ gan } 为天干；二者均可 */
    const list = [];
    function push(name, basis, value, luck, desc) {
        list.push({
            name,
            basis,
            value,
            luck,
            desc,
            inChuan: chuanSet.has(value),
            inKe: keSet.has(value),
        });
    }
    // ── 1. 天乙贵人（日干；据占时取昼贵/夜贵） ──
    {
        const [day, night] = guiRen(dayGanIndex);
        const idx = isDayTime(zhanShiIndex) ? day : night;
        push('天乙贵人', '日干', zhiOf(idx), 2, '一切凶煞遇贵则解，主贵人提携、逢难有救');
    }
    // ── 2. 天德（月支；值天干月份时为天干） ──
    {
        const v = TIAN_DE[yueZhiIndex];
        push('天德', '月支', v, 2, '德神，主解厄消灾、转凶为吉');
    }
    // ── 3. 月德（月支三合局） ──
    {
        const v = YUE_DE[juOf(yueZhiIndex)];
        push('月德', '月支', v, 2, '德神，主慈祥福厚、凶事得解');
    }
    // ── 4~9. 三合局系神煞（按日支取） ──
    {
        const row = SAN_HE_SHEN_SHA[juOf(dayZhiIndex)];
        push('驿马', '日支', zhiOf(row.yiMa), 1, '主变动、出行、迁转、奔驰之事');
        push('桃花', '日支', zhiOf(row.taoHua), 0, '主情缘、娱乐、酒色，亦主信息快捷');
        push('华盖', '日支', zhiOf(row.huaGai), 0, '主孤高、艺术、宗教、聪明脱俗');
        push('将星', '日支', zhiOf(row.jiangXing), 1, '主威权、统率、担当重任');
        push('劫煞', '日支', zhiOf(row.jieSha), -2, '主劫夺、凶险、被盗、意外之失');
        push('灾煞', '日支', zhiOf(row.zaiSha), -2, '主灾病、官非、横事之扰');
    }
    // ── 10~11. 羊刃 / 飞刃（日干） ──
    {
        const ren = YANG_REN[dayGanIndex];
        push('羊刃', '日干', zhiOf(ren), -2, '刚烈之煞，主争斗、伤损、急躁生祸');
        push('飞刃', '日干', zhiOf(feiRen(dayGanIndex)), -1, '羊刃之冲，主暗伤、突发之变');
    }
    // ── 12. 破碎（日支） ──
    push('破碎', '日支', zhiOf(poSui(dayZhiIndex)), -1, '主破损、耗散、事情不完整');
    // ── 13. 天喜（月支所属季） ──
    push('天喜', '月支', zhiOf(tianXi(yueZhiIndex)), 2, '主喜庆、婚姻、生育、财喜之事');
    // ── 14. 天马（月支） ──
    push('天马', '月支', zhiOf(tianMa(yueZhiIndex)), 1, '主远行、迁动、升迁、消息远来');
    // ── 15~16. 红鸾 / 天哭（年支） ──
    push('红鸾', '年支', zhiOf(hongLuan(nianZhiIndex)), 2, '主婚姻、喜事、吉庆之兆');
    push('天哭', '年支', zhiOf(tianKu(nianZhiIndex)), -1, '主悲忧、哭泣、孝服之事');
    // ── 17~18. 病符 / 死符（年支） ──
    push('病符', '年支', zhiOf(bingFu(nianZhiIndex)), -1, '主疾病、体弱、精神不振');
    push('死符', '年支', zhiOf(siFu(nianZhiIndex)), -2, '主丧亡、大凶，逢之宜静守');
    // ── 19~20. 孤辰 / 寡宿（年支） ──
    {
        const { gu, gua } = guGua(nianZhiIndex);
        if (gu >= 0)
            push('孤辰', '年支', zhiOf(gu), -1, '主孤独、亲缘淡薄、独断独行');
        if (gua >= 0)
            push('寡宿', '年支', zhiOf(gua), -1, '主寡合、冷清、婚姻迟滞');
    }
    // 天干类神煞（天德/月德）若为天干字面，则命中判定以天干比对三传遁干，此处不额外标记。
    return list.sort((a, b) => Math.abs(b.luck) - Math.abs(a.luck));
}
/** 神煞命中判定：该神煞是否落在给定地支集合（三传 / 四课上神） */
export function hitShenSha(s, zhiList) {
    return zhiList.includes(s.value);
}
/** 调试辅助：神煞所值是否为天干 */
export function isGanValue(s) {
    return GAN.includes(s.value) && !ZHI.includes(s.value);
}
/** 展示辅助：天干索引 → 干名（供外部需要时使用） */
export function ganName(i) {
    return ganOf(i);
}
//# sourceMappingURL=shensha.js.map